import {
  HHAlert,
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  checkPopKeyframes,
  MOTION_DURATION,
  MOTION_EASING,
  MOTION_STAGGER_MAX_ITEMS,
  MOTION_STAGGER_MS,
  stepEnterKeyframes,
  usePrefersReducedMotion,
} from '../components/motion/motion';
import { ContractTreeView } from '../components/summary/contract-tree-view';
import { useStepHeader } from '../context/wizard-header.context';
import { useWizardState } from '../context/wizard-state.context';
import { useValidationSuite } from '../hooks/use-validation-suite';
import { buildContractTree } from '../utils/build-contract-tree';

const monoText = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 12,
};

const PASS_COLOR = '#198754';
const FAIL_COLOR = '#dc3545';

export const DoneStep = (): JSX.Element => {
  const { state, reset } = useWizardState();
  const navigate = useNavigate();
  const reduced = usePrefersReducedMotion();
  const { checks, running, rerun } = useValidationSuite();

  useStepHeader({
    description:
      'Validation suite — auto-running checks against the data we just wrote. Failures don’t auto-rollback; the Repair CTA links you back to the right earlier step.',
  });
  const tree = useMemo(
    () => buildContractTree({ state, stage: 'done' }),
    [state],
  );
  if (state.input == null) return <Navigate to="../intake" replace />;
  if (!state.commit.completed) return <Navigate to="../review" replace />;

  const passed = checks.filter(c => c.status === 'pass').length;
  const failed = checks.filter(c => c.status === 'fail').length;

  return (
    <HHStack spacing={3}>
      <HHAlert
        hhVariant="outlined"
        severity={failed === 0 ? 'success' : 'warning'}
      >
        <HHStack direction="row" spacing={2} alignItems="baseline">
          <HHTypography hhVariant="subtitle2">
            {running
              ? 'Running validation…'
              : `${passed} passed${failed > 0 ? `, ${failed} failed` : ''}`}
          </HHTypography>
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            color="primary"
            size="small"
            onClick={rerun}
          >
            Re-run
          </HHButton>
        </HHStack>
      </HHAlert>

      <HHStack spacing={1}>
        <HHTypography hhVariant="subtitle1">Final state</HHTypography>
        <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
          What now exists across KG, Customer Accounts, and Provider Network —
          with the IDs minted by this run. Each Contract subtree owns its
          Customer Account, Merchant, Locations, and Practitioners.
        </HHTypography>
        <ContractTreeView tree={tree} />
      </HHStack>

      <HHStack spacing={1}>
        <HHTypography hhVariant="subtitle1">Validation</HHTypography>
        {checks.map((c, i) => {
          const borderColor =
            c.status === 'pass'
              ? PASS_COLOR
              : c.status === 'fail'
                ? FAIL_COLOR
                : 'rgba(0,0,0,0.2)';
          return (
            <Accordion
              key={c.id}
              variant="outlined"
              disableGutters
              sx={{
                borderLeft: `3px solid ${borderColor}`,
                // Staggered fade-up as the suite renders; the per-item delay is
                // capped so a long check list never feels sluggish.
                animation: reduced
                  ? 'none'
                  : `${stepEnterKeyframes} ${MOTION_DURATION.state}ms ${MOTION_EASING.standard} both`,
                animationDelay: reduced
                  ? undefined
                  : `${Math.min(i, MOTION_STAGGER_MAX_ITEMS) * MOTION_STAGGER_MS}ms`,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <HHStack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <span
                    style={{
                      width: 16,
                      display: 'inline-flex',
                      justifyContent: 'center',
                      color:
                        c.status === 'pass'
                          ? PASS_COLOR
                          : c.status === 'fail'
                            ? FAIL_COLOR
                            : undefined,
                    }}
                  >
                    {c.status === 'pending' ? (
                      <CircularProgress size={12} thickness={5} />
                    ) : (
                      // Fresh mount when the check resolves, so the glyph pops
                      // in to acknowledge the pass/fail result.
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          animation: reduced
                            ? 'none'
                            : `${checkPopKeyframes} ${MOTION_DURATION.state}ms ${MOTION_EASING.standard} both`,
                        }}
                      >
                        {c.status === 'pass' ? '✓' : '✗'}
                      </Box>
                    )}
                  </span>
                  <HHTypography
                    hhVariant="body2"
                    sx={{ opacity: c.status === 'pending' ? 0.6 : 1 }}
                  >
                    {c.label}
                  </HHTypography>
                  {c.status === 'fail' && c.repairStep && (
                    <span style={{ flex: 1, textAlign: 'right' }}>
                      <HHButton
                        hhVariant="variant-bypass"
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={(e): void => {
                          e.stopPropagation();
                          navigate(`../${c.repairStep}`);
                        }}
                      >
                        Repair →
                      </HHButton>
                    </span>
                  )}
                </HHStack>
              </AccordionSummary>
              <AccordionDetails>
                <HHStack spacing={1}>
                  <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                    Request
                  </HHTypography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1, ...monoText, whiteSpace: 'pre-wrap' }}
                  >
                    {JSON.stringify(c.request, null, 2)}
                  </Paper>
                  {c.response != null && (
                    <>
                      <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                        Response
                      </HHTypography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          ...monoText,
                          whiteSpace: 'pre-wrap',
                          maxHeight: 240,
                          overflow: 'auto',
                        }}
                      >
                        {JSON.stringify(c.response, null, 2)}
                      </Paper>
                    </>
                  )}
                  {c.error != null && (
                    <HHTypography
                      hhVariant="caption"
                      sx={{ color: FAIL_COLOR }}
                    >
                      {c.error}
                    </HHTypography>
                  )}
                </HHStack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </HHStack>

      <HHStack direction="row" spacing={2}>
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          color="primary"
          onClick={(): void => {
            reset();
            navigate('../intake');
          }}
        >
          Start a new contract
        </HHButton>
      </HHStack>
    </HHStack>
  );
};
